import * as React from 'react';
import { useMemo } from 'react';
import { MoreHorizontalIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { buttonVariants } from '../../components/ui/button';

export const usePagination = ({ totalPages, currentPage, siblingCount = 0 }) => {
  const paginationRange = useMemo(() => {
    const totalPageNumbers = siblingCount + 5;

    if (totalPages <= totalPageNumbers) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftEllipsis = leftSiblingIndex > 2;
    const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, '...', totalPages];
    }

    if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = Array.from({ length: rightItemCount }, (_, i) => totalPages - rightItemCount + i + 1);
      return [firstPageIndex, '...', ...rightRange];
    }

    if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
      let middleRange = [];
      for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
        middleRange.push(i);
      }
      return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
    }
  }, [totalPages, currentPage, siblingCount]);

  return paginationRange;
};

function Pagination({ className, ...props }) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className={cn(
        'mx-auto flex w-full justify-center px-2 sm:px-0', // add padding on small screens
        className
      )}
      {...props}
    />
  );
}

function PaginationContent({ className, ...props }) {
  return (
    <ul
      className={cn(
        'flex items-center gap-1',
        'text-sm sm:text-base', // smaller font on mobile
        'flex-wrap justify-center', // allow wrapping on small screens
        className
      )}
      {...props}
    />
  );
}

function PaginationItem(props) {
  return <li {...props} />;
}

function PaginationLink({ className, isActive, size = 'icon', ...props }) {
  return (
    <a
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        buttonVariants({ variant: 'ghost', size }),
        'rounded-full min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm px-2 sm:px-3 py-1', // smaller button on mobile
        {
          'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]': isActive,
        },
        className
      )}
      {...props}
    />
  );
}

function PaginationPrevious({ className, disabled, ...props }) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      aria-disabled={disabled}
      className={cn(
        'gap-1 border rounded-full px-2.5 sm:px-3 py-1 text-xs sm:text-sm',
        disabled && 'opacity-50 pointer-events-none',
        className
      )}
      {...props}
    >
      <span>Prev</span>
    </PaginationLink>
  );
}

function PaginationNext({ className, disabled, ...props }) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      aria-disabled={disabled}
      className={cn(
        'gap-1 border rounded-full px-2.5 sm:px-3 py-1 text-xs sm:text-sm',
        disabled && 'opacity-50 pointer-events-none',
        className
      )}
      {...props}
    >
      <span>Next</span>
    </PaginationLink>
  );
}

function PaginationEllipsis({ className, ...props }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-7 sm:size-9 items-center justify-center text-sm sm:text-base',
        className
      )}
      {...props}
    >
      <MoreHorizontalIcon className="size-4 sm:size-5" />
      <span className="sr-only">More pages</span>
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
